// Оверлей встроенного раздела (docs/api-ui-v3.md §6, api-gaps п. 12): правка
// «✎» встроенного раздела пишет черновик в том же формате. Из опубликованного
// оверлея клиент применяет во вшитом виде:
//   - `sidebar` — дополнительные разделы бокового меню (под вшитым меню);
//   - `actions` с place:"panel" — кнопки строкой в шапке раздела (у вшитых
//     видов свои плавающие панели, чужие кнопки в них не встраиваются);
//   - `canvas.blocks` — общим SectionRenderer над вшитым видом (или под ним —
//     `props.place: "bottom"`), с потолком высоты и своей прокруткой.
// В Беседах, Мессенджерах и Конструкторе рабочая область занята целиком (CLI,
// полотно схемы) — блоки там не встраиваются; у черновика это сказано в шапке,
// а сами блоки видны в предпросмотре `draft:<id>`.
// Пока есть черновик — над разделом шапка «принять / отменить / версии».

import type { SectionModule } from '../types'
import { DraftBar } from './DraftBar'
import { useSectionModel } from './model'
import { OverlayActions, OverlayBlocks, SectionSidebar } from './SectionRenderer'
import { useSections } from './store'
import type { SectionModel } from './types'

/** Встроенные разделы, куда блоки полотна оверлея встраиваются без ломки вида. */
const BLOCKS_EMBED = new Set(['feed', 'tracker', 'notes', 'library'])

const TITLES: Record<string, string> = {
  chats: 'Беседы', feed: 'Лента', flow: 'Конструктор', tracker: 'Трекер', notes: 'Заметки',
  messengers: 'Мессенджеры', library: 'Библиотеки',
}

/** Пометка у черновика: что из оверлея этот раздел не покажет во вшитом виде. */
export function overlayNote(id: string, title: string, model: SectionModel | null | undefined): string | null {
  if (!model?.canvas?.blocks?.length || BLOCKS_EMBED.has(id)) return null
  return `полотно оверлея в «${TITLES[id] ?? title}» не встраивается — рабочая область занята целиком; `
    + 'после публикации видны меню и кнопки, блоки — только в предпросмотре'
}

function usePublished(id: string): boolean {
  return useSections((s) => !!s.list.find((x) => x.id === id)?.published)
}

function OverlaySidebar({ id }: { id: string }) {
  return usePublished(id) ? <OverlaySidebarLoaded id={id} /> : null
}

function OverlaySidebarLoaded({ id }: { id: string }) {
  const entry = useSectionModel(id, false)
  if (!entry?.model?.sidebar?.length) return null
  return <SectionSidebar sectionId={id} model={entry.model} draft={false} />
}

function OverlayBar({ id, title }: { id: string; title: string }) {
  const summary = useSections((s) => s.list.find((x) => x.id === id))
  const draft = useSectionModel(summary?.hasDraft ? id : null, true)
  if (!summary?.hasDraft) return null
  return <DraftBar id={id} title={`${title} · правка`} preview={false} note={overlayNote(id, title, draft?.model)} />
}

function Published({ id, place }: { id: string; place: 'top' | 'bottom' | 'actions' }) {
  const entry = useSectionModel(id, false)
  const model = entry?.model
  if (!model) return null
  if (place === 'actions') return <OverlayActions sectionId={id} model={model} />
  if (!BLOCKS_EMBED.has(id)) return null
  return <OverlayBlocks sectionId={id} model={model} place={place} />
}

export function withOverlay(id: string, title: string, mod: SectionModule): SectionModule {
  const Inner = mod.Sidebar
  const InnerView = mod.View
  function Sidebar() {
    return (
      <>
        <Inner />
        <OverlaySidebar id={id} />
      </>
    )
  }
  function View() {
    const published = usePublished(id)
    return (
      <>
        <OverlayBar id={id} title={title} />
        {published ? <Published id={id} place="actions" /> : null}
        {published ? <Published id={id} place="top" /> : null}
        <InnerView />
        {published ? <Published id={id} place="bottom" /> : null}
      </>
    )
  }
  return { ...mod, Sidebar, View }
}
